import data from "../data/questions.json";
import meta from "../data/sections.json";
import { LESSONS } from "../data/lessons";
import { SUBTOPICS } from "../data/subtopics";

// Recense les schémas (mini-quiz) présents dans chaque section, par niveau.
const DIAGRAMS_BY_SECTION = (() => {
  const out = {};
  for (const level of Object.keys(LESSONS)) {
    out[level] = {};
    for (const code of Object.keys(LESSONS[level])) {
      const names = (LESSONS[level][code].slides || [])
        .map((s) => s.diagram)
        .filter(Boolean);
      if (names.length) out[level][code] = names;
    }
  }
  return out;
})();
export const getSectionDiagramCount = (level, code) =>
  ((DIAGRAMS_BY_SECTION[level] || {})[code] || []).length;

export const META = meta;
export const getQuestions = (level) => data[level];
export const getBySection = (level, code) =>
  data[level].filter((q) => q.section === code);
export const getById = (level, id) => data[level].find((q) => q.id === id);
export const getSectionMeta = (level, code) =>
  meta[level].sections.find((s) => s.code === code);

const KEY = "hamprep_progress_v1";
const GOAL_KEY = "hamprep_daily_goal";
const BOX_INTERVALS = [0, 1, 3, 7, 16]; // jours avant re-révision selon la boîte (1..5)

const todayKey = () => new Date().toISOString().slice(0, 10);
const addDays = (n) => {
  const d = new Date();
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
};

export function loadProgress() {
  try {
    return JSON.parse(localStorage.getItem(KEY)) || {};
  } catch (e) {
    console.warn("Progression locale illisible, réinitialisation:", e);
    return {};
  }
}
let _onChange = null;
export function setOnChange(fn) {
  _onChange = fn;
}
function save(p) {
  localStorage.setItem(KEY, JSON.stringify(p));
  if (_onChange) {
    try { _onChange(); } catch (e) { console.warn("Callback de synchronisation échoué:", e); }
  }
}
function ensure(p, level) {
  if (!p[level]) p[level] = { questions: {}, exams: [], days: [] };
  if (!p[level].days) p[level].days = [];
  if (!p[level].favorites) p[level].favorites = [];
  if (!p[level].daily) p[level].daily = {};
  if (!p[level].diagrams) p[level].diagrams = {};
  return p;
}
function markToday(p, level) {
  const d = todayKey();
  if (!p[level].days.includes(d)) p[level].days.push(d);
}

export function recordAnswer(level, qid, correct) {
  const p = loadProgress();
  ensure(p, level);
  const q = p[level].questions[qid] || { c: 0, w: 0, box: 1 };
  if (correct) q.c += 1;
  else q.w += 1;
  q.last = correct ? "c" : "w";
  // Répétition espacée (Leitner)
  const box = q.box || 1;
  q.box = correct ? Math.min(5, box + 1) : 1;
  q.due = addDays(BOX_INTERVALS[q.box - 1]);
  p[level].questions[qid] = q;
  // Objectif quotidien
  const tk = todayKey();
  p[level].daily[tk] = (p[level].daily[tk] || 0) + 1;
  markToday(p, level);
  save(p);
}

export function recordExam(level, result) {
  const p = loadProgress();
  ensure(p, level);
  p[level].exams.unshift({ ...result, date: new Date().toISOString() });
  p[level].exams = p[level].exams.slice(0, 50);
  markToday(p, level);
  save(p);
}

export function resetProgress(level) {
  const p = loadProgress();
  p[level] = { questions: {}, exams: [], days: [], favorites: [], daily: {} };
  save(p);
}

export function completeModule(level, code) {
  const p = loadProgress();
  ensure(p, level);
  if (!p[level].course) p[level].course = [];
  if (!p[level].course.includes(code)) p[level].course.push(code);
  markToday(p, level);
  save(p);
}

export function getCourse(level) {
  const p = loadProgress();
  return (p[level] && p[level].course) || [];
}

// Progression relative AU MODULE : toutes les questions de la section + ses schémas.
export function getModuleProgress(level, code) {
  const secQs = getBySection(level, code);
  const store = loadProgress()[level] || {};
  const q = store.questions || {};
  let qMastered = 0;
  let qAnswered = 0;
  for (const item of secQs) {
    const st = q[item.id];
    if (st) {
      qAnswered += 1;
      if (st.last === "c") qMastered += 1;
    }
  }
  const dg = getSectionDiagramProgress(level, code); // { total, correct }
  const qTotal = secQs.length;
  const total = qTotal + dg.total;
  const mastered = qMastered + dg.correct;
  return {
    total,
    mastered,
    pct: total ? Math.round((mastered / total) * 100) : 0,
    qTotal,
    qMastered,
    qAnswered,
    diagTotal: dg.total,
    diagCorrect: dg.correct,
  };
}

// Ids des questions d'une section déjà répondues au moins une fois (pour la reprise).
export function getSectionAnsweredIds(level, code) {
  const store = loadProgress()[level] || {};
  const q = store.questions || {};
  return getBySection(level, code).filter((x) => q[x.id]).map((x) => x.id);
}

// Découpe une section en mini-chapitres (sous-thèmes officiels) avec titre + progression.
export function getSubtopics(level, code) {
  const secQs = getBySection(level, code);
  const store = loadProgress()[level] || {};
  const q = store.questions || {};
  const titles = (SUBTOPICS[level] || {});
  const order = [];
  const groups = {};
  for (const item of secQs) {
    const sub = item.id.split("-").slice(0, 3).join("-");
    if (!groups[sub]) {
      groups[sub] = { sub, title: titles[sub] || "Chapitre", questions: [], answered: 0, mastered: 0 };
      order.push(sub);
    }
    groups[sub].questions.push(item);
    const st = q[item.id];
    if (st) {
      groups[sub].answered += 1;
      if (st.last === "c") groups[sub].mastered += 1;
    }
  }
  return order.map((sub, i) => {
    const g = groups[sub];
    const total = g.questions.length;
    return {
      ...g,
      index: i,
      total,
      done: g.answered === total,
      pct: total ? Math.round((g.mastered / total) * 100) : 0,
      minutes: Math.max(2, Math.round(total * 0.6)), // ~35 s par question
    };
  });
}

// Points (XP) = total des bonnes réponses maîtrisées pour le niveau.
export function getLevelXP(level) {
  const store = loadProgress()[level] || {};
  const q = store.questions || {};
  let xp = 0;
  for (const id in q) if (q[id].last === "c") xp += 10;
  return xp;
}

// Dernier chapitre ouvert (pour « Reprendre là où j'en étais »).
export function recordLastChapter(level, code, sub) {
  const p = loadProgress();
  ensure(p, level);
  p[level].lastChapter = { code, sub };
  save(p);
}
export function getLastChapter(level) {
  const p = loadProgress();
  return (p[level] && p[level].lastChapter) || null;
}
// Prochain chapitre non terminé d'un module (chapitre recommandé).
export function getNextChapter(level, code) {
  const chapters = getSubtopics(level, code);
  return chapters.find((c) => !c.done) || null;
}

// Badges nouvellement débloqués (pour notification) — n'alerte pas au tout premier appel.
export function getNewlyEarnedBadges(level) {
  const p = loadProgress();
  ensure(p, level);
  const badges = getBadges(level);
  const earnedIds = badges.filter((b) => b.earned).map((b) => b.id);
  if (!p[level].seenBadges) {
    p[level].seenBadges = earnedIds;
    save(p);
    return [];
  }
  const seen = new Set(p[level].seenBadges);
  const newly = badges.filter((b) => b.earned && !seen.has(b.id));
  if (newly.length) {
    p[level].seenBadges = earnedIds;
    save(p);
  }
  return newly;
}
// Initialise l'instantané des badges au démarrage (sans notifier).
export function initSeenBadges(level) {
  const p = loadProgress();
  ensure(p, level);
  if (!p[level].seenBadges) {
    p[level].seenBadges = getBadges(level).filter((b) => b.earned).map((b) => b.id);
    save(p);
  }
}

// Horodatage de début de module (pour le temps passé).
export function markModuleStart(level, code) {
  const p = loadProgress();
  ensure(p, level);
  if (!p[level].moduleStart) p[level].moduleStart = {};
  if (!p[level].moduleStart[code]) {
    p[level].moduleStart[code] = Date.now();
    save(p);
  }
}
export function getModuleStart(level, code) {
  const p = loadProgress();
  return (p[level] && p[level].moduleStart && p[level].moduleStart[code]) || null;
}
export function getBadges(level) {
  const stats = getStats(level);
  const completedModules = getCourse(level).length;
  const totalModules = meta[level].sections.length;
  const xp = getLevelXP(level);
  let perfectModules = 0;
  for (const s of meta[level].sections) {
    const mp = getModuleProgress(level, s.code);
    if (mp.total > 0 && mp.mastered === mp.total) perfectModules += 1;
  }
  return [
    { id: "first-steps", icon: "Footprints", label: "Premiers pas", desc: "Répondre à 10 questions", earned: stats.answered >= 10 },
    { id: "streak-3", icon: "Flame", label: "Sur une lancée", desc: "3 jours d'affilée", earned: stats.streak >= 3 },
    { id: "module-1", icon: "BookCheck", label: "Premier module", desc: "Terminer 1 module", earned: completedModules >= 1 },
    { id: "perfect-1", icon: "Target", label: "Sans faute", desc: "Maîtriser 100 % d'un module", earned: perfectModules >= 1 },
    { id: "streak-7", icon: "Flame", label: "Assidu", desc: "7 jours d'affilée", earned: stats.streak >= 7 },
    { id: "module-3", icon: "BookCheck", label: "En route", desc: "Terminer 3 modules", earned: completedModules >= 3 },
    { id: "xp-500", icon: "Star", label: "500 points", desc: "Cumuler 500 points", earned: xp >= 500 },
    { id: "streak-30", icon: "CalendarCheck", label: "Marathonien", desc: "30 jours d'affilée", earned: stats.streak >= 30 },
    { id: "module-all", icon: "GraduationCap", label: "Cursus complet", desc: "Terminer tous les modules", earned: completedModules >= totalModules },
    { id: "xp-2000", icon: "Trophy", label: "Expert", desc: "Cumuler 2000 points", earned: xp >= 2000 },
  ];
}

// Reprise dans le lecteur de cours : mémorise l'étape/diapo en cours.
export function saveCoursePosition(level, code, pos) {
  const p = loadProgress();
  ensure(p, level);
  if (!p[level].coursePos) p[level].coursePos = {};
  p[level].coursePos[code] = pos; // { phase, slide }
  save(p);
}
export function getCoursePosition(level, code) {
  const p = loadProgress();
  return (p[level] && p[level].coursePos && p[level].coursePos[code]) || null;
}
export function clearCoursePosition(level, code) {
  const p = loadProgress();
  if (p[level] && p[level].coursePos && p[level].coursePos[code]) {
    delete p[level].coursePos[code];
    save(p);
  }
}

/* ---------- Mini-quiz des schémas ---------- */
export function recordDiagramQuiz(level, section, name, correct, selectedText) {
  const p = loadProgress();
  ensure(p, level);
  p[level].diagrams[name] = { section, correct: !!correct, selected: selectedText || "" };
  const tk = todayKey();
  p[level].daily[tk] = (p[level].daily[tk] || 0) + 1;
  markToday(p, level);
  save(p);
}
export function getDiagramResult(level, name) {
  const p = loadProgress();
  return (p[level] && p[level].diagrams && p[level].diagrams[name]) || null;
}
// { total, correct } des schémas d'une section
export function getSectionDiagramProgress(level, code) {
  const total = getSectionDiagramCount(level, code);
  const store = loadProgress()[level] || {};
  const dg = store.diagrams || {};
  let correct = 0;
  for (const name in dg) {
    if (dg[name].section === code && dg[name].correct) correct += 1;
  }
  return { total, correct };
}

/* ---------- Favoris ---------- */
export function toggleFavorite(level, qid) {
  const p = loadProgress();
  ensure(p, level);
  const arr = p[level].favorites;
  const i = arr.indexOf(qid);
  if (i >= 0) arr.splice(i, 1);
  else arr.push(qid);
  save(p);
  return i < 0; // true si ajouté
}
export function getFavorites(level) {
  const p = loadProgress();
  return (p[level] && p[level].favorites) || [];
}
export function getFavoriteQuestions(level) {
  const set = new Set(getFavorites(level));
  return data[level].filter((q) => set.has(q.id));
}

/* ---------- Objectif quotidien ---------- */
export function getGoal() {
  return parseInt(localStorage.getItem(GOAL_KEY) || "20", 10);
}
export function setGoal(n) {
  localStorage.setItem(GOAL_KEY, String(n));
}
export function getTodayCount(level) {
  const p = loadProgress();
  return (p[level] && p[level].daily && p[level].daily[todayKey()]) || 0;
}

/* ---------- Révision espacée ---------- */
export function getDueCount(level) {
  const p = loadProgress();
  const lp = p[level] || { questions: {} };
  const t = todayKey();
  let n = 0;
  for (const id in lp.questions) {
    const st = lp.questions[id];
    if (st.due && st.due <= t) n += 1;
  }
  return n;
}
// File de révision : questions dues d'abord, complétées par les plus faibles
export function pickReview(level, count) {
  const p = loadProgress();
  const lp = p[level] || { questions: {} };
  const t = todayKey();
  const due = [];
  const weak = [];
  for (const q of data[level]) {
    const st = lp.questions[q.id];
    if (!st) continue;
    if (st.due && st.due <= t) due.push({ q, due: st.due });
    else if (st.last === "w") weak.push(q);
  }
  due.sort((a, b) => (a.due < b.due ? -1 : 1));
  let list = due.map((x) => x.q);
  if (list.length < count) list = list.concat(shuffle(weak));
  return list.slice(0, count);
}

function computeStreak(days) {
  if (!days || !days.length) return 0;
  const set = new Set(days);
  let streak = 0;
  const d = new Date();
  for (;;) {
    const key = d.toISOString().slice(0, 10);
    if (set.has(key)) {
      streak += 1;
      d.setDate(d.getDate() - 1);
    } else if (streak === 0) {
      d.setDate(d.getDate() - 1);
      const y = d.toISOString().slice(0, 10);
      if (set.has(y)) {
        streak += 1;
        d.setDate(d.getDate() - 1);
      } else break;
    } else break;
  }
  return streak;
}

export function readinessLabel(pct) {
  if (pct >= 80) return { label: "Prêt", tone: "success" };
  if (pct >= 50) return { label: "Presque", tone: "warning" };
  return { label: "À travailler", tone: "muted" };
}

export function getStats(level) {
  const p = loadProgress();
  const lp = p[level] || { questions: {}, exams: [], days: [] };
  const qs = data[level];
  const total = qs.length;
  let answered = 0;
  let mastered = 0;
  let totalC = 0;
  let totalW = 0;
  const secMap = {};
  for (const s of meta[level].sections)
    secMap[s.code] = { code: s.code, name: s.name, total: 0, answered: 0, mastered: 0 };
  for (const q of qs) {
    const st = lp.questions[q.id];
    secMap[q.section].total += 1;
    if (st) {
      answered += 1;
      totalC += st.c || 0;
      totalW += st.w || 0;
      secMap[q.section].answered += 1;
      if (st.last === "c") {
        mastered += 1;
        secMap[q.section].mastered += 1;
      }
    }
  }
  const attempts = totalC + totalW;
  return {
    total,
    answered,
    mastered,
    accuracy: attempts ? Math.round((totalC / attempts) * 100) : 0,
    progress: total ? Math.round((mastered / total) * 100) : 0,
    exams: lp.exams || [],
    streak: computeStreak(lp.days),
    daysStudied: (lp.days || []).length,
    sections: Object.values(secMap),
    favorites: (lp.favorites || []).length,
    dueCount: getDueCount(level),
  };
}

// weighted question picking for practice: prioritize unseen + wrong
export function pickPractice(level, count, sectionCode) {
  const p = loadProgress();
  const lp = p[level] || { questions: {} };
  let pool = sectionCode ? getBySection(level, sectionCode) : getQuestions(level);
  const scored = pool.map((q) => {
    const st = lp.questions[q.id];
    let weight = 3; // unseen
    if (st) weight = st.last === "w" ? 4 : 1;
    return { q, weight, r: Math.random() * weight };
  });
  scored.sort((a, b) => b.r - a.r);
  return scored.slice(0, Math.min(count, pool.length)).map((x) => x.q);
}

export function pickExam(level) {
  const cfg = meta[level];
  const perSection = {};
  const bySec = {};
  for (const s of cfg.sections) {
    bySec[s.code] = shuffle(getBySection(level, s.code));
    perSection[s.code] = Math.max(
      1,
      Math.round((s.count / cfg.total) * cfg.examQuestions)
    );
  }
  let picked = [];
  for (const s of cfg.sections)
    picked = picked.concat(bySec[s.code].slice(0, perSection[s.code]));
  if (picked.length < cfg.examQuestions) {
    const usedIds = new Set(picked.map((q) => q.id));
    const rest = shuffle(getQuestions(level).filter((q) => !usedIds.has(q.id)));
    picked = picked.concat(rest.slice(0, cfg.examQuestions - picked.length));
  }
  picked = shuffle(picked);
  if (picked.length > cfg.examQuestions) picked = picked.slice(0, cfg.examQuestions);
  return picked;
}

/* ---------- Recherche ---------- */
export function searchQuestions(level, query, limit = 60) {
  const q = query.trim().toLowerCase();
  if (!q) return [];
  const out = [];
  for (const item of data[level]) {
    if (
      item.question.toLowerCase().includes(q) ||
      item.options.some((o) => o.toLowerCase().includes(q)) ||
      item.id.toLowerCase().includes(q)
    ) {
      out.push(item);
      if (out.length >= limit) break;
    }
  }
  return out;
}

/* ---------- Export / Import / Sync ---------- */
export function exportData() {
  return JSON.stringify({ progress: loadProgress(), goal: getGoal() }, null, 2);
}
export function importData(str) {
  const obj = JSON.parse(str);
  if (obj.progress) save(obj.progress);
  if (obj.goal) setGoal(obj.goal);
  return true;
}
export function getSyncPayload() {
  return { progress: loadProgress(), goal: getGoal() };
}
// Applique les données serveur SANS redéclencher la synchronisation (pas de _onChange).
export function applyServerData(data) {
  if (data && data.progress) localStorage.setItem(KEY, JSON.stringify(data.progress));
  if (data && typeof data.goal === "number") localStorage.setItem(GOAL_KEY, String(data.goal));
}

export function shuffle(arr) {
  const a = arr.slice();
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}
